/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import mbpUtil from 'mbp-api-util';
import React, { useEffect, useState, useContext } from 'react';
import Media from 'react-media';
import {
    object, bool, string, func, number, shape, arrayOf,
} from 'prop-types';
import { compose } from 'recompose';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

// Material UI
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';

// Helpers
import configENV from '../../config';
import Helper, { getChannelUser } from '../../state/reducers/Helper';
import withPageView from '../../../../helpers/tracking/hocs/withPageView';
import EventDispatcher from '../../state/reducers/EventDispatcher';
import CUSTOM_BREAKPOINTS from '../../style/customBreakpoints';
import { withAuth } from '../../../../../state/ducks/Member/ducks/Auth/Plugin/auth';
import { trackPage } from '../../../../helpers/tracking/common/commonTrackingHelpers';
import { isEmpty } from '../../utils/object';
import { getFeatureFlags } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { OrderDetailsStyle } from '../../style/OrderDetailsStyle';
import { getParamsFromCryptedURL, decipher } from '../../utils/crypto';
import { getIsAuthenticatedStatus, getProfileInfo } from '../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getBrandIdByHostName } from '../../../../../state/ducks/App/App-Helpers';
import { getAccessTokenSafely } from '../../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import getSiteConfig from '../../utils/getSiteConfig';
import { OrderDetailsContext } from './Context/OrderDetailsContext';
import { getSSRDeviceType } from '../../../../../state/ducks/App/App-Selectors';

// Components
import DeliveryHours from '../TrackOrder/DeliveryHours';
import ItemDetail from './ItemDetail';
import BackButton from '../../common/BackButton';
import BillingDetail from './BillingDetail';
import BillingDetailGuest from './BillingDetailGuest';
import Infos from './Sections/Infos';
import OrderNumber from './Sections/OrderNumber';
import DesktopOrderDetails from '../DesktopOrderDetails/DesktopOrderDetails';
import CancelOrderModal from './SelfService/Desktop/CancelOrderModal';
import ErrorPage from '../../common/ErrorPage';
import GraphqlSeoHeadTags from '../../../../components/GraphqlComponents/GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import GenericSkeleton from '../../../../components/GraphqlComponents/GraphqlSkeletonComponents/GenericSkeleton';

const {
    FEDEX_ORDERTRACKING_URL, UPS_ORDERTRACKING_URL, USPS_ORDERTRACKING_URL, UDS_ORDERTRACKING_URL,
} = configENV;

let brand = '';

if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    brand = getBrandIdByHostName(hostname);
}
const styles = ({ typography, palette, breakpoints }) => OrderDetailsStyle(typography, palette, breakpoints, brand);

const OrderDetails = (props) => {
    const {
        match: {
            params: {
                uuid, order, zipcode, billingzipcode,
            },
        }, classes, breakpoint, auth, track, userProfile, userProfile: { isAuthenticated }, zipCode,
    } = props;

    const {
        ROSSOrder, orderDataLoaded, errorMessage, createFetchOrderDetailsMutation, getUuid,
    } = useContext(OrderDetailsContext);

    const dispatch = useDispatch();
    const history = useHistory();
    const [openModal, setOpenModal] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const { orderNumber, billingZipCode, redirectURL } = useSelector((state) => state.account);
    const ssrDeviceType = useSelector(getSSRDeviceType);
    const isAuthenticatedStatus = useSelector(getIsAuthenticatedStatus);
    const featureFlags = useSelector(getFeatureFlags);
    const profile = useSelector(getProfileInfo);

    const isCancelByItemVariantEnabled = featureFlags['which-cancel-by-line-item-variant']; // This flag is responsible to check line item cancel turned on/off per product brand
    const wholeOrderCancelFeatureFlag = featureFlags['is-whole-order-cancel-enabled'];
    const lineItemCancelFeatureFlag = featureFlags['which-cancel-by-line-item-variant']; // This flag is responsible to check line item cancel turned on/off per product brand
    const isCancelReasonEnabled = featureFlags['is-cancel-reason-enabled'];
    const isCancelRefundAmountDisplayed = featureFlags['is-cancel-refund-amount-displayed'];
    const isWebUsingNewChannelCode = featureFlags['is-web-using-new-channel-codes'];

    const handleFetchOrderDetails = async (orderNumberFromURL, zipCodeFromURL, billingZipCodeFromURL, options) => {
        sessionStorage.removeItem('ROSSOrder');
        sessionStorage.removeItem('orderDetailsLoaded');

        let orderNumber = orderNumberFromURL;
        let zipCode = zipCodeFromURL;
        let billingZipCode = billingZipCodeFromURL;

        if (options) {
            if (uuid && options.zip && options.zip !== '0' && !orderNumber) {
                zipCode = decipher(uuid.slice(0, 8))(options.zip);
            }
            if (uuid && options.zip && options.zip !== '0' && !orderNumber) {
                billingZipCode = decipher(uuid.slice(0, 8))(options.zip);
            }
            if (uuid && options.order && !orderNumber) {
                orderNumber = decipher(uuid.slice(0, 8))(options.order.replace(/-/g, ''));
            }
        }

        getUuid(uuid);
        const accessToken = await getAccessTokenSafely();
        const user = getChannelUser(ssrDeviceType, getSiteConfig('domain'), isWebUsingNewChannelCode);
        const email = profile?.email || userProfile?.email;
        const rossData = isAuthenticatedStatus
            ? { user, orderNumber, email }
            : {
                user, orderNumber, recipientZipCode: zipCode, billingZipCode,
            };
        const internalToken = (user.indexOf('30001') > -1) ? mbpUtil.getEnv('ROSS_TOKEN_DESKTOP') : mbpUtil.getEnv('ROSS_TOKEN_MOBILE');
        createFetchOrderDetailsMutation.mutate({ jwt: accessToken, requestBody: rossData, header: internalToken });
    };

    useEffect(() => {
        // Added update = true for graphQL to query order details
        // after successfully validated recipient and billing zip code
        // eslint-disable-next-line object-curly-newline
        const options = order ? { order, zipcode, billingzipcode, update: true } : null;
        let orderNumberFromURL = orderNumber;
        let zipCodeFromURL = zipCode;
        let billingZipCodeFromURL = billingZipCode;

        // Override orderNumber from order details' URL
        if (typeof window !== 'undefined') {
            if (!isAuthenticated && isAuthenticatedStatus) {
                // Force redirect to '/account/orders' after fresh login
                // to update `userProfile` object

                // commenting as it userProfile is getting updated on login
                // without redirecting to '/account/orders'

                // window.location.href = '/account/orders';
            } else {
                const pathName = window.location.pathname;
                // Override orderNumber from URL
                // for both logged in or guest users
                if (order) {
                    const { orderNumber } = getParamsFromCryptedURL(pathName);
                    orderNumberFromURL = orderNumber;
                }
                // Override zipCode and billingZipCode from URL if they are available
                // only for guest users
                if (zipcode && billingzipcode) {
                    const { zipCode, billingZipCode } = getParamsFromCryptedURL(pathName);
                    billingZipCodeFromURL = billingZipCode;
                    zipCodeFromURL = zipCode;
                }
            }
        }

        const isFirstLoad = sessionStorage.getItem('isFirstLoad');

        if ((uuid && orderNumberFromURL && isAuthenticatedStatus) || (isFirstLoad && !isAuthenticatedStatus)) {
            handleFetchOrderDetails(orderNumberFromURL, zipCodeFromURL, billingZipCodeFromURL, options);
        }

        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }

        if (!isAuthenticatedStatus) {
            sessionStorage.setItem('isFirstLoad', true);
        }

        return () => {
            sessionStorage.removeItem('isFirstLoad');
            sessionStorage.removeItem('ROSSOrder');
            sessionStorage.removeItem('orderDetailsLoaded');
        };
    }, [isAuthenticated]);

    useEffect(() => {
        const loggedFlag = sessionStorage.getItem('hasLoggedReloaded');
    
        if (!isAuthenticated && isAuthenticatedStatus && !isLoggedIn && !loggedFlag) {
            sessionStorage.setItem('hasLoggedReloaded', 'true');
            setIsLoggedIn(true);
            history.go();
        }

        return () => {
            sessionStorage.removeItem('hasLoggedReloaded');
        };
    }, [isAuthenticatedStatus]);

    // eslint-disable-next-line object-curly-newline
    const errorScreen = ({ classes, orderDataLoaded, breakpointExpr, isAuthenticatedStatus }, errors) => (
        <>
            {orderDataLoaded && (
                <Media query={breakpointExpr}>
                    {(matches) => (
                        matches ? (
                            // Mobile view
                            <div className="ross-mobile-order-header">
                                <h1>
                                    <BackButton url="/account/orders" />
                                    Order Details
                                </h1>
                                <>
                                    <DeliveryHours />
                                    <ErrorPage errors={errors} />
                                </>
                            </div>
                        )
                            :                         (
                        // Desktop view
                                <Grid item xs={10} className={!isAuthenticatedStatus ? 'order-detail-guest' : ''} style={{ padding: '40px 37px 0 43px', margin: !isAuthenticatedStatus ? '0 auto' : '' }}>
                                    <div className={classes.OrderDetailsStyle}>
                                        <div className="desktop-view">
                                            <Grid container>
                                                <Grid className="position-relative" item xs={6}>
                                                    <h2 className="dekstop-order-details-title">
                                                        Order Details
                                                    </h2>
                                                </Grid>
                                                <Grid className="top-button-wrapper" item xs={6}>
                                                    {isAuthenticatedStatus && <BackButton url="/account/orders" desktopHDButton />}
                                                </Grid>
                                            </Grid>
                                            <ErrorPage errors={errors} />
                                        </div>
                                    </div>
                                </Grid>
                            )
                    )}
                </Media>
            )}
        </>
    );

    const print = () => {
        if (typeof window !== 'undefined') {
            dispatch(trackEvent({
                eventCategory: 'Order Details Page',
                eventAction: 'Print',
                eventLabel: ROSSOrder?.orderID || '',
            }));
            window.print();
        }
    };

    const renderTrackingLink = (carrier, trackingNumber, deliveryLink = '', productBrandCode = '') => {
        if (productBrandCode === '1036' || productBrandCode === '1038') {
            return trackingNumber;
        }

        if (deliveryLink !== '') {
            return (
                <a
                    href={deliveryLink}
                    target="_blank"
                    rel="noreferrer"
                    className="bold"
                >
                    {trackingNumber}
                </a>
            );
        }

        let carrierUrl;
        switch (carrier) {
            case 'FEDEX':
                carrierUrl = FEDEX_ORDERTRACKING_URL;
                break;
            case 'UPS':
                carrierUrl = UPS_ORDERTRACKING_URL;
                break;
            case 'USPS':
                carrierUrl = USPS_ORDERTRACKING_URL;
                break;
            case 'UDS':
                carrierUrl = UDS_ORDERTRACKING_URL;
                break;
            default:
                carrierUrl = null;
        }
        return carrier ? (
            <a
                href={carrierUrl + trackingNumber}
                target="_blank"
                rel="noreferrer"
                className="bold"
            >
                {trackingNumber}
            </a>
        ) : (
            <span>{trackingNumber}</span>
        );
    };

    const handleChangeModalState = async () => {
        let redirectToLogin = false;
        const isKMSIEnabled = featureFlags?.['is-keep-me-signed-in-redirect-enabled'] === true || false;
        if (isKMSIEnabled) {
            // Handle redirect to login if login required on click of cancel order button
            redirectToLogin = await Helper.redirectToLogin(featureFlags, auth.loginByRedirect);
        }

        if (!redirectToLogin) {
            if (openModal) {
                setOpenModal(false);
                history.go();
            } else {
                setOpenModal(true);
            }
        }
    };

    const isEligibleForCancel = () => {
        // condition for cancel by WHOLE ORDER:
        //     1. feature flag is-web-cancel-enabled ==> true
        //     2. passes product brand check, isAllowedBrand ==> true
        //     3. passes ROSS cancel flag check   ROSSOrder.cancelFlag ==> true
        //     4. Donation product not allowed to be cancel

        const isWebCancelEnabled = featureFlags['is-web-cancel-enabled']; // Flag responsible to turn on/off Cancel functionality(line item cancel & whole order cancel both)
        const brandsCancelFeatureFlag = featureFlags['which-product-brand-allows-cancel'];
        const orderLevelCancelFlag = ROSSOrder?.cancelFlag;
        const isWholeOrderCancelEnabled = wholeOrderCancelFeatureFlag && orderLevelCancelFlag;
        let shouldShowCancelButton = isWholeOrderCancelEnabled;
        let orderHasCancelNotAllowedBrand = false;
        let orderHasDonationOrPassport = false;

        if (Array.isArray(ROSSOrder?.orders)) {
            const {
                checkIsPassportItem,
                checkIsDonationItem,
                isFoodBrand,
                isFloralBrand,
            } = Helper;

            ROSSOrder.orders.forEach((lineItem) => {
                const productName = lineItem?.items?.[0]?.productName;
                const firstItemProductBrand = lineItem?.items?.[0]?.productBrand; // assumption is made here about all one line item will never have different product brands
                const lineItemLevelCancelFlag = lineItem?.cancelFlag;
                const pendingCancel = lineItem?.pendingCancel;
                // if product is either donation or passport items, do not show cancel button
                if (checkIsDonationItem(productName) || checkIsPassportItem(productName)) {
                    return orderHasDonationOrPassport = true;
                }

                if (!brandsCancelFeatureFlag[firstItemProductBrand]) {
                    return orderHasCancelNotAllowedBrand = true;
                }

                if (isFoodBrand(firstItemProductBrand) && !isFloralBrand(firstItemProductBrand)) {
                    // this scenario is whole order cancel only
                    shouldShowCancelButton = isWholeOrderCancelEnabled;
                }

                if (isFloralBrand(firstItemProductBrand) && !isFoodBrand(firstItemProductBrand)) {
                    // this scenario qualifies for both line item cancel and line item cancel flag
                    if (lineItemLevelCancelFlag === 'Y' && !pendingCancel && lineItemCancelFeatureFlag[firstItemProductBrand]) {
                        shouldShowCancelButton = true;
                    }
                }

                // TODO: clean up this if statement
                if (!Object.values(lineItemCancelFeatureFlag).includes(true) && isWholeOrderCancelEnabled) {
                    shouldShowCancelButton = true;
                }
            });
        }
        const isShowCancelWholeOrderButton = isWebCancelEnabled && shouldShowCancelButton && !orderHasDonationOrPassport && !orderHasCancelNotAllowedBrand;
        return isShowCancelWholeOrderButton;
    };

    const fetchIsHDOrderEditable = (ROSSOrder = {}) => {
        /* HD editable check logic:
         - if pass atlas intOrderNumber, pass 0 in recipientId
         - if pass BCS number (brandOrderNum), pass the correct recipientSeqNumber
        */
        const { intOrderNumber } = ROSSOrder;
        const { productBrand, productName, itemLineId } = ROSSOrder.items?.[0];
        const isFEHDEditableEnabled = featureFlags['is-web-frontend-calling-hd-editable'] || false; // additional check to verify flag is enabled or not

        if (isFEHDEditableEnabled && Helper.isHDBrand(productBrand) && !productName.match(/passport annual membership|passport for|passport at/gi)) {
            const payload = {
                orderId: intOrderNumber,
                brandOrderNo: intOrderNumber,
                recipientId: '0',
                itemLineId,
            };
            dispatch(EventDispatcher.handleFetchHDEditableCheck(payload));
        }
        return null;
    };

    const isShowingTrackingWidget = (ROSSItem, ROSSOrder) => {
        const isROSSOrderLoadedFromCheckout = ROSSOrder?.loadedFromTopic || false;
        const isROSSItemGiftBand = ROSSItem?.subItemType === 'B';
        const isROSSItemGCI = ROSSItem?.addonType === 'GCI';
        const isROSSItemADDON = ROSSItem?.addonType === 'ADDON';
        const isROSSItemChildTypeA = ROSSItem?.subItemType === 'A';
        const isROSSItemChildType2 = ROSSItem?.subItemType === '2';
        const firstROSSItemSubItemType = ROSSOrder?.items?.[0]?.subItemType || '';
        const firstROSSItemAddonType = ROSSOrder?.items?.[0]?.addonType || '';
        const isROSSItemStatusShown = !(isROSSItemGiftBand || isROSSItemGCI || isROSSItemADDON);

        // If Loaded from checkout - we check for the parent and child using addonType only - subItemType field is not available from checkout

        // Identify PYO Child
        // Display: Tracker on PYO_PARENT *** BUT don't show on children with (subItemType 'A' or 'B') OR (addonType: 'GCI' or addonType: 'ADDON')
        const isROSSItemPYOChild = isROSSOrderLoadedFromCheckout
            // Loaded From Topic Check
            ? firstROSSItemAddonType === 'PYO_PARENT'
            // Loaded From ROSS Check
            : (firstROSSItemAddonType === 'PYO_PARENT' && firstROSSItemSubItemType === '6'
               // Children & Other subItemType / addonType check
               && (isROSSItemChildTypeA));

        // Identify: CYO Child
        // Display: Tracker on CYO_PARENT *** BUT don't show on children with (subItemType 'A' or 'B') OR (addonType: 'GCI' or addonType: 'ADDON')
        const isROSSItemCYOChild = isROSSOrderLoadedFromCheckout
            // Loaded From Topic Check
            ? firstROSSItemAddonType === 'CYO_PARENT'
            // Loaded From ROSS Check
            : (firstROSSItemAddonType === 'CYO_PARENT' && (firstROSSItemSubItemType === '4' || firstROSSItemSubItemType === '6')
               // Children & Other subItemType / addonType check
               && (isROSSItemChildTypeA));

        // Identify: COL Child
        // Display: Tracker on COL_PARENT *** BUT don't show on children with (subItemType 'A' or 'B') OR (addonType: 'GCI' or addonType: 'ADDON')
        const isROSSItemCOLChild = isROSSOrderLoadedFromCheckout
            // Loaded From Topic Check
            ? firstROSSItemAddonType === 'COL_PARENT'
            // Loaded From ROSS Check
            : (firstROSSItemAddonType === 'COL_PARENT' && (firstROSSItemSubItemType === '2' || firstROSSItemSubItemType === '4')
               // Children & Other subItemType / addonType check
               && (isROSSItemChildTypeA));

        // Identify: CLUB Child
        // Display: Tracker on CLUB_PAR *** BUT don't show on children with (subItemType 'A' or 'B') OR (addonType: 'GCI' or addonType: 'ADDON')
        const isROSSItemClubChild = isROSSOrderLoadedFromCheckout
            // Loaded From Topic Check
            ? firstROSSItemAddonType === 'CLUB_PAR'
            // Loaded From ROSS Check
            : (firstROSSItemAddonType === 'CLUB_PAR' && (firstROSSItemSubItemType === '2' || firstROSSItemSubItemType === '4')
               // Children & Other subItemType / addonType check
               && (isROSSItemChildTypeA));

        // Identify: Fruit of The Month Parent
        // Display: Tracker on Fruit of the Month Children *** BUT don't show on Fruit of the Month Parent OR children with (subItemType: 'B') OR (addonType: 'GCI' or addonType: 'ADDON')
        const isROSSItemFOMCParent = isROSSOrderLoadedFromCheckout
            // Loaded From Topic Check
            ? firstROSSItemAddonType === 'CLUB_PAR'
            // Loaded From ROSS Check
            : (firstROSSItemAddonType === 'CLUB_PAR' && firstROSSItemSubItemType === '3'
               // Children & Other subItemType / addonType check
               && ((isROSSItemChildTypeA || isROSSItemChildType2)));

        // We only hide status tracker if below conditions are false - otherwise we show it
        return (!isROSSItemPYOChild && !isROSSItemCYOChild && !isROSSItemCOLChild && !isROSSItemFOMCParent && !isROSSItemClubChild) && isROSSItemStatusShown;
    };

    const getIsOrderContainsFoodProduct = () => {
        let isOrderContainsFoodItem = false;
        ROSSOrder?.orders?.forEach((order) => {
            order.items.forEach((item) => {
                if (Helper.isFoodBrand(item.productBrand)) {
                    return isOrderContainsFoodItem = true;
                }
            });
        });
        return isOrderContainsFoodItem;
    };

    const intOrderNumberList = [];
    const isOrderEligibleForCancel = isEligibleForCancel();

    // this object has the flags for controlling desktop and mobile delivery image preview / thumbs
    const deliveryImageFlags = {
        desktop: {
            preview: featureFlags['is-web-showing-delivery-image-preview'],
            thumbs: featureFlags['is-delivery-image-showing-thumbs'],
        },
        mobile: {
            preview: featureFlags['is-mobile-showing-delivery-image-preview'],
            thumbs: featureFlags['is-mobile-delivery-image-showing-thumbs'],
        },
    };

    const { isHDBrand, redirectToLogin } = Helper;
    const breakpointExpr = `(max-width: ${(breakpoint || CUSTOM_BREAKPOINTS.tabLandscape)}px)`;
    // Loading screen
    if (!orderDataLoaded) {
        return (
            <>
                <GraphqlSeoHeadTags seoData={{
                    page_title: 'Order Details',
                    location: { pathname: history?.location?.pathname || '' },
                }}
                />
                <GenericSkeleton height="800px" width={ssrDeviceType === 'mobile' ? '100%' : '80%'} />
            </>
        );
    }
    if (redirectURL && typeof redirectURL === 'string') {
        history.push(redirectURL);
        return null;
    }
    if (errorMessage || featureFlags['is-ross-error-page-enabled'] === true) {
        const error = {};
        error.isErrorResponse = !!errorMessage;
        error.errorMessage = errorMessage;
        error.component = `Order Details-${ssrDeviceType}`;
        return errorScreen({
            classes,
            orderDataLoaded,
            breakpointExpr,
            isAuthenticatedStatus,
        },
        error, // error = { isErrorResponse: true, errorMessage: ' ... ', module: 'Order Details-Desktop'}
        );
    }

    const title = 'Order Details';
    const tiEmail = ROSSOrder?.orders?.[0]?.billingProfile?.emailAddress;
    const isOrderContainsFoodProduct = getIsOrderContainsFoodProduct();

    return (
        <>
            {orderDataLoaded && (
                <Media query={breakpointExpr}>
                    {(matches) => (
                        matches
                            ? (
                        // Mobile view
                                <div className={classes.OrderDetailsStyle}>
                                    <div className="mobile-view">
                                        <div className={isOrderEligibleForCancel ? 'ross-mobile-order-header-with-cancel' : 'ross-mobile-order-header-no-cancel'}>
                                            <div className={isOrderEligibleForCancel ? 'backButtonWrap' : ''}>
                                                <BackButton url="/account/orders" />
                                            </div>
                                            <OrderNumber ROSSOrder={ROSSOrder} />
                                            {
                                                isOrderEligibleForCancel && (
                                                    <Grid className="cancelButtonWrap">
                                                        <Button
                                                            variant="container"
                                                            onClick={handleChangeModalState}
                                                            className="cta-button"
                                                            style={{ textTransform: 'none' }}
                                                        >
                                                            Cancel Order
                                                        </Button>
                                                    </Grid>
                                                )
                                            }
                                            {
                                                openModal && (
                                                    <CancelOrderModal
                                                        handleChangeModalState={handleChangeModalState}
                                                        orderNumber={ROSSOrder?.orderID}
                                                        isCancelReasonEnabled={isCancelReasonEnabled}
                                                        isCancelRefundAmountDisplayed={isCancelRefundAmountDisplayed}
                                                        orderTotal={ROSSOrder?.orderTotal}
                                                        tiEmail={tiEmail}
                                                        lineItemOrders={ROSSOrder?.orders}
                                                        wholeOrderCancelFlag={ROSSOrder?.cancelFlag}
                                                        isCancelByItemVariantEnabled={isCancelByItemVariantEnabled}
                                                        isOrderContainsFoodProduct={isOrderContainsFoodProduct}
                                                        track={track}
                                                        lineItemCancelFeatureFlag={lineItemCancelFeatureFlag}
                                                        wholeOrderCancelFeatureFlag={wholeOrderCancelFeatureFlag}
                                                    />
                                                )
                                            }
                                        </div>
                                        {!isEmpty(ROSSOrder) && ROSSOrder?.orders?.map((order, ind) => {
                                            intOrderNumberList.push(order.intOrderNumber);
                                            return (
                                                <ItemDetail
                                                    key={order.intOrderNumber}
                                                    uuid={uuid}
                                                    ROSSOrder={ROSSOrder}
                                                    index={ind}
                                                    isMultiItemOrder={ROSSOrder.orders.length > 1}
                                                    renderTrackingLink={renderTrackingLink}
                                                    ROSSModifyOrder={ROSSOrder?.modifyOrder}
                                                    handleRedirectToLogin={redirectToLogin}
                                                    loginByRedirect={auth.loginByRedirect}
                                                    isHDBrand={isHDBrand}
                                                    fetchIsHDOrderEditable={fetchIsHDOrderEditable}
                                                    isShowingTrackingWidget={isShowingTrackingWidget}
                                                    isOrderContainsFoodProduct={isOrderContainsFoodProduct}
                                                    deliveryImageFlags={deliveryImageFlags.mobile}
                                                />
                                            );
                                        })}

                                        {isAuthenticatedStatus ? (
                                            <BillingDetail featureFlags={featureFlags} ROSSOrder={ROSSOrder} />
                                        ) : (
                                            <BillingDetailGuest ROSSOrder={ROSSOrder} />
                                        )}

                                        <Infos ENABLE_MMO={featureFlags['is-account-mmo-enabled']} />

                                        <div className="padding-bottom-2x" />
                                    </div>
                                </div>
                            )
                            :                         (
                        // Desktop view
                                <Grid item xs={10} style={{ padding: '40px 37px 0 43px', margin: !isAuthenticatedStatus ? '0 auto' : '' }}>
                                    <div className={classes.OrderDetailsStyle}>
                                        <div className="desktop-view">
                                            <Grid container>
                                                <Grid className="position-relative" item xs={5}>
                                                    <h2 className="dekstop-order-details-title">
                                                        {title}
                                                    </h2>
                                                </Grid>
                                                <Grid className="top-button-wrapper" item xs={7}>
                                                    <Button
                                                        onClick={() => print()}
                                                        variant="contained"
                                                        className={`cta-button ${!isAuthenticatedStatus ? 'margin-right-0' : ''}`}
                                                        style={{ textTransform: 'none' }}
                                                    >
                                                        Print
                                                    </Button>
                                                    {
                                                        isOrderEligibleForCancel && (
                                                            <Grid>
                                                                <Button
                                                                    variant="container"
                                                                    onClick={handleChangeModalState}
                                                                    className="cta-button"
                                                                    style={{ textTransform: 'none', margin: '0px 10px 0px 0px' }}
                                                                >
                                                                    Cancel Order
                                                                </Button>
                                                            </Grid>
                                                        )
                                                    }
                                                    {
                                                        openModal && (
                                                            <CancelOrderModal
                                                                handleChangeModalState={handleChangeModalState}
                                                                orderNumber={ROSSOrder?.orderID}
                                                                isCancelReasonEnabled={isCancelReasonEnabled}
                                                                isCancelRefundAmountDisplayed={isCancelRefundAmountDisplayed}
                                                                orderTotal={ROSSOrder?.orderTotal}
                                                                tiEmail={tiEmail}
                                                                lineItemOrders={ROSSOrder?.orders}
                                                                wholeOrderCancelFlag={ROSSOrder?.cancelFlag}
                                                                isCancelByItemVariantEnabled={isCancelByItemVariantEnabled}
                                                                isOrderContainsFoodProduct={isOrderContainsFoodProduct}
                                                                track={track}
                                                                lineItemCancelFeatureFlag={lineItemCancelFeatureFlag}
                                                                wholeOrderCancelFeatureFlag={wholeOrderCancelFeatureFlag}
                                                            />
                                                        )
                                                    }
                                                    {isAuthenticatedStatus && <BackButton url="/account/orders" desktopBackLink />}
                                                </Grid>
                                            </Grid>
                                            <DesktopOrderDetails
                                                uuid={uuid}
                                                ROSSOrder={ROSSOrder}
                                                isAuthenticatedStatus={isAuthenticatedStatus}
                                                handleRedirectToLogin={redirectToLogin}
                                                loginByRedirect={auth.loginByRedirect}
                                                isHDBrand={isHDBrand}
                                                fetchIsHDOrderEditable={fetchIsHDOrderEditable}
                                                isShowingTrackingWidget={isShowingTrackingWidget}
                                                isOrderContainsFoodProduct={isOrderContainsFoodProduct}
                                                deliveryImageFlags={deliveryImageFlags.desktop}
                                            />
                                        </div>
                                    </div>
                                </Grid>
                            )
                    )}
                </Media>
            )}
        </>
    );
};

OrderDetails.defaultProps = {
    match: {},
    history: {},
    breakpoint: CUSTOM_BREAKPOINTS.tabLandscape,
    ROSSOrder: {},
    error: {},
    isErrorResponse: false,
    redirectURL: '',
    track: () => {},
};

OrderDetails.propTypes = {
    classes: object.isRequired,
    history: object,
    userProfile: shape({
        accessToken: string,
        idToken: string,
        ContactId: string,
        userType: string,
        userRole: string,
        email: string,
        aud: string,
        iat: number,
        exp: number,
        sub: string,
        firstName: string,
        lastName: string,
        loginsCount: number,
        isAuthenticated: bool,
        isGuestAccount: bool,
    }).isRequired,
    match: object,
    zipCode: string,
    billingZipCode: string,
    breakpoint: number,
    ROSSOrder: shape({
        brandId: string,
        modifiedDateTime: string,
        orderNumber: string,
        orders: arrayOf(shape({
            carrier: string,
            deliveryNotes: string,
            history: arrayOf(shape({
                codeStatus: string,
                createdDate: string,
                location: object,
                status: string,
                statusDescription: string,
            })),
            intOrderNumber: string,
        })),
        items: arrayOf(shape({
            productBrand: string,
            itemType: string,
            productName: string,
            productSku: string,
        })),
        origin: string,
        recipientFirstName: string,
        recipientLastName: string,
        signatureImageURL: string,
        status: string,
        statusDescription: string,
        trackingNumber: string,
        type: string,
        warningMessage: string,
    }),
    error: object,
    isErrorResponse: bool,
    orderDataLoaded: bool,
    auth: shape({
        loginByRedirect: func.isRequired,
    }).isRequired,
    track: func,
};

const enhance = compose(
    trackPage({
        type: 'ordertrackingdetail',
        name: 'OrderTrackingSearch',
        accountPage: true,
    }),
    withAuth,
    withStyles(styles, { withTheme: true }),
    withPageView({
        pageViewSuccessTest: () => true,
    }),
);
export default enhance(OrderDetails);
